import { makeAutoObservable, runInAction } from "mobx";

import { showToast } from "@/lib/utils/toast";
import { t } from "i18next";
import { shareSurveyService } from "../services";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class ShareSurveyStore {
  open = false;
  survey = null;
  mode = "guest";
  inviteSearch = "";
  inviteSuggestions = [];
  suggestionsLoading = false;
  selectedInvites = new Set();
  inviteLoading = false;
  guestEmail = "";
  guestMessage = "";
  guestEmailError = "";
  internalError = "";
  inviteUrl = "";
  inviteResults = [];

  constructor() {
    makeAutoObservable(this);
  }

  get selectedInvitesCount() {
    return this.selectedInvites.size;
  }
  clearInvites = () => {
    this.selectedInvites.clear();
  };
  clearInviteResults() {
    this.inviteResults = [];
  }

  setSurvey = (survey) => {
    this.survey = survey || null;
  };
  setInviteUrl = (url) => {
    this.inviteUrl = url;
  };

  resetState = () => {
    this.mode = "guest";
    this.inviteSearch = "";
    this.inviteSuggestions = [];
    this.suggestionsLoading = false;
    this.selectedInvites = new Set();
    this.inviteLoading = false;
    this.guestEmail = "";
    this.guestMessage = "";
    this.guestEmailError = "";
  };

  clear = () => {
    this.resetState();
    this.survey = null;
  };

  openDialog = async (survey) => {
    if (!survey) return;
    this.open = true;
    await this.initialize(survey);
  };

  closeDialog = () => {
    this.open = false;
    this.clear();
  };

  initialize = async (survey) => {
    this.setSurvey(survey);
    this.setInviteUrl("");
    this.resetState();
    await this.fetchSuggestions();
  };

  setMode = (value) => {
    this.mode = value;
  };

  setInviteSearch = (value) => {
    this.inviteSearch = value;
  };

  setGuestEmail = (value) => {
    this.guestEmail = value;
    if (value.trim() && this.isInternalEmail(value)) {
      this.guestEmailError = t(
        "This email belongs to an internal user. Please use the Internal Users tab instead.",
      );
    } else {
      this.guestEmailError = "";
    }
  };

  setGuestMessage = (value) => {
    this.guestMessage = value;
  };

  isInternalEmail = (email) => {
    const normalizedEmail = email.trim().toLowerCase();
    return this.inviteSuggestions.some(
      (user) => user.email?.toLowerCase() === normalizedEmail,
    );
  };

  toggleInternal = (email) => {
    if (!email) return;
    const next = new Set(this.selectedInvites);
    if (next.has(email)) {
      next.delete(email);
    } else {
      next.add(email);
    }
    this.selectedInvites = next;
    this.internalError = "";
  };

  sendInternalInvites = async () => {
    if (!this.survey?._id) return false;
    const emails = Array.from(this.selectedInvites);
    if (!emails.length) {
      showToast.error(t("Select at least one internal user."));
      return false;
    }

    runInAction(() => {
      this.inviteLoading = true;
    });

    try {
      const res = await shareSurveyService.inviteSurvey(this.survey._id, {
        scope: "internal",
        allowExternalRespondents: false,
        inviteEmails: emails,
      });
      if (!res) {
        throw new Error(t("Unable to send invites."));
      }

      showToast.success(
        t("Invited {{count}} internal users.", { count: emails.length }),
      );
      runInAction(() => {
        console.log("result", JSON.stringify(res.deliveries));
        this.inviteResults = res.deliveries ?? []; // ← store delivery data
        this.clearInvites();
        this.selectedInvites = new Set();
        this.internalError = "";
      });
      return true;
    } catch (error) {
      const message = error?.message || t("Unable to send internal invites.");
      runInAction(() => {
        this.internalError = message;
      });
      showToast.error(message);
      return false;
    } finally {
      runInAction(() => {
        this.inviteLoading = false;
      });
    }
  };

  sendGuestInvite = async () => {
    if (!this.survey?._id) return false;
    const email = this.guestEmail.trim().toLowerCase();
    if (!emailRegex.test(email)) {
      showToast.error(t("Enter a valid guest email."));
      return false;
    }
    if (this.isInternalEmail(email)) {
      showToast.error(
        t(
          "This email belongs to an internal user. Please use the Internal Users tab.",
        ),
      );
      return false;
    }

    runInAction(() => {
      this.inviteLoading = true;
    });

    try {
      const data = await shareSurveyService.inviteSurvey(this.survey._id, {
        scope: "public",
        allowExternalRespondents: true,
        inviteEmails: [email],
        message: this.guestMessage || undefined,
      });
      if (!data) {
        throw new Error(t("Unable to send guest invite."));
      }
      const inviteUrl = data.deliveries?.[0]?.inviteUrl;
      showToast.success(t("Guest invite sent."));
      runInAction(() => {
        this.guestEmail = "";
        this.guestMessage = "";
        this.inviteUrl = inviteUrl;
      });
      return true;
    } catch (error) {
      showToast.error(error?.message || t("Unable to send guest invite."));
      return false;
    } finally {
      runInAction(() => {
        this.inviteLoading = false;
      });
    }
  };
  fetchSuggestions = async () => {
    if (!this.survey?._id) return;
    runInAction(() => {
      this.suggestionsLoading = true;
    });

    try {
      const params = {};
      const response = await shareSurveyService.fetchInviteSuggestions(params, {
        showErrorToast: false,
      });
      const data = response?.data || response;
      runInAction(() => {
        const raw = data?.result || [];
        const seen = new Set();
        this.inviteSuggestions = raw.filter((r) => {
          if (!r.email || seen.has(r.email)) return false;
          seen.add(r.email);
          return true;
        });
      });
    } catch (error) {
      runInAction(() => {
        this.inviteSuggestions = [];
        showToast.error(
          error?.message || t("Unable to load user suggestions."),
        );
      });
    } finally {
      runInAction(() => {
        this.suggestionsLoading = false;
      });
    }
  };
}

export const shareSurveyStore = new ShareSurveyStore();
